// Phase 3A.6 — bare slug catch-all referral resolver.
// =====================================================
// When a visitor hits `https://www.liftedatthemoment.com/{affiliateSlug}`
// (anything that didn't match a defined route), we:
//   1. Resolve the slug against `/api/r/{slug}/resolve` (cheap, no redirect).
//   2. Store attribution in localStorage + a 30-day cookie.
//   3. Redirect cleanly to the homepage `/`.
// If the slug doesn't resolve, redirect to `/?ref_invalid=1` (existing behavior).

import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

const REF_KEY = "lifted_ref";
const REF_COOKIE = "lifted_ref";

function setRefCookie(code, days = 30) {
  const expires = new Date(Date.now() + days * 86400 * 1000).toUTCString();
  document.cookie = `${REF_COOKIE}=${encodeURIComponent(
    code,
  )}; expires=${expires}; path=/; SameSite=Lax`;
}

export default function AffiliateSlugRedirect() {
  const { slug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/r/${encodeURIComponent(slug)}/resolve`, {
          credentials: "include",
        });
        if (!r.ok) {
          if (!cancelled) navigate("/?ref_invalid=1", { replace: true });
          return;
        }
        const body = await r.json().catch(() => ({}));
        if (body?.code) {
          try {
            localStorage.setItem(
              REF_KEY,
              JSON.stringify({
                code: body.code,
                slug: body.slug || slug,
                first_touch_at: new Date().toISOString(),
                last_touch_at: new Date().toISOString(),
                source: `/${slug}`,
              }),
            );
          } catch {
            /* localStorage disabled — cookie still works */
          }
          setRefCookie(body.code);
        }
        if (!cancelled) navigate("/", { replace: true });
      } catch {
        if (!cancelled) navigate("/", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-lifted-deep text-lifted-dim text-sm"
      data-testid="affiliate-slug-redirect"
    >
      Loading Lifted…
    </div>
  );
}
